import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Image, Play, X, Type, Sparkles, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { aiAPI, mediaAPI, getErrorMessage } from '../../services/api';
import type { MediaFile } from './types';

// Hard caps to protect the renderer from OOM crashes (Chrome error code 5)
const MAX_IMAGE_BYTES = 25 * 1024 * 1024; // 25 MB
const MAX_VIDEO_BYTES = 200 * 1024 * 1024; // 200 MB

interface Props {
  mediaFiles: MediaFile[];
  setMediaFiles: React.Dispatch<React.SetStateAction<MediaFile[]>>;
}

export const MediaUploadTab: React.FC<Props> = ({ mediaFiles, setMediaFiles }) => {
  const { theme } = useThemeStore();
  const { t } = useTranslation();
  const [editingAlt, setEditingAlt] = React.useState<string | null>(null);
  const [describing, setDescribing] = useState(false);

  // Upload the staged file if not already uploaded, returning its
  // server media id. Caches the id on the MediaFile so subsequent
  // actions (AI describe, save flow) can reuse it without re-upload.
  const ensureUploaded = async (id: string): Promise<string | null> => {
    const current = mediaFiles.find((f) => f.id === id);
    if (!current) {
      return null;
    }
    if (current.mediaId) {
      return current.mediaId;
    }
    try {
      const resp = await mediaAPI.directUpload(current.file, () => {});
      const newId = resp.data?.media_id;
      if (!newId) {
        return null;
      }
      setMediaFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, mediaId: newId } : f))
      );
      return newId;
    } catch (err: unknown) {
      toast.error(
        getErrorMessage(
          err,
          t('compose_upload_failed', 'Upload failed. Please try again.')
        )
      );
      return null;
    }
  };

  const handleAutoDescribe = async (id: string) => {
    setDescribing(true);
    try {
      const mediaId = await ensureUploaded(id);
      if (!mediaId) {
        return;
      }
      const { data } = await aiAPI.describeImage(mediaId);
      const description = (data && (data.description || data.alt_text)) || '';
      if (!description) {
        toast.error(t('compose_alt_ai_empty', 'Model returned no description'));
        return;
      }
      setMediaFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, altText: description } : f))
      );
      toast.success(t('compose_alt_ai_generated', 'Alt text generated'));
    } catch (err: unknown) {
      toast.error(
        getErrorMessage(
          err,
          t('compose_alt_ai_failed', 'Failed to generate alt text')
        )
      );
    } finally {
      setDescribing(false);
    }
  };

  // Track blob URLs created by this component so we can revoke on unmount
  // even if the parent state is reset or the component is removed mid-flight.
  const createdUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const urls = createdUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    // Only allow a single file — take the first one and replace any existing
    const file = acceptedFiles[0];
    const isVideo = file.type.startsWith('video/');
    const limit = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
    if (file.size > limit) {
      const mb = Math.round(limit / (1024 * 1024));
      toast.error(
        t('compose_file_too_large', 'File is too large (max {{mb}} MB)', { mb })
      );
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    createdUrlsRef.current.add(previewUrl);
    const newFile: MediaFile = {
      id: Math.random().toString(36).substring(7),
      file,
      preview: previewUrl,
      type: isVideo ? 'video' : 'image',
    };
    // Revoke old previews
    setMediaFiles((prev) => {
      prev.forEach((f) => {
        URL.revokeObjectURL(f.preview);
        createdUrlsRef.current.delete(f.preview);
      });
      return [newFile];
    });
    toast.success(t('compose_file_added', 'File added'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMediaFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi', '.mkv'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const removeMediaFile = (id: string) => {
    setMediaFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.preview);
        createdUrlsRef.current.delete(file.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const updateAltText = (id: string, altText: string) => {
    setMediaFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, altText } : f))
    );
  };

  const media = mediaFiles[0] || null;

  return (
    <>
      {/* Dropzone — show when no file attached */}
      {!media && (
        <div
          {...getRootProps()}
          style={{
            borderColor: isDragActive ? theme.hex.primary : '#e5e7eb',
            backgroundColor: isDragActive ? theme.hex.primaryLight : '#f9fafb',
          }}
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
        >
          <input {...getInputProps()} />
          <Image className="mx-auto h-10 w-10 text-gray-300 mb-2" />
          {isDragActive ? (
            <p style={{ color: theme.hex.primaryText }} className="text-sm font-medium">{t('compose_drop_file_here', 'Drop your file here...')}</p>
          ) : (
            <>
              <p className="text-gray-600 text-sm font-medium">{t('compose_drag_drop_media', 'Drag and drop media here')}</p>
              <p className="text-gray-400 text-xs mt-1">{t('compose_supported_formats', 'JPG, PNG, GIF, WebP, MP4, MOV')}</p>
            </>
          )}
        </div>
      )}

      {/* Single file preview */}
      {media && (
        <div className="mt-2">
          <div className="relative group rounded-lg overflow-hidden border border-gray-200">
            {media.type === 'image' ? (
              <img src={media.preview} alt={media.altText || t('compose_preview_alt', 'Preview')} className="w-full h-40 object-cover" />
            ) : (
              <video
                src={media.preview}
                className="w-full h-40 object-cover"
                muted
                preload="metadata"
                playsInline
              >
                <track kind="captions" />
              </video>
            )}

            {media.type === 'video' && <Play className="absolute top-2 right-2 h-5 w-5 text-white drop-shadow" />}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-lg transition-all flex items-center justify-center gap-3">
              <button
                onClick={() => removeMediaFile(media.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-black bg-opacity-60 rounded-lg"
                title={t('compose_remove', 'Remove')}
              >
                <X className="h-4 w-4 text-white" />
              </button>
              {media.type === 'image' && (
                <button
                  onClick={() => setEditingAlt(editingAlt === media.id ? null : media.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-black bg-opacity-60 rounded-lg"
                  title={t('compose_edit_alt_text', 'Edit alt text')}
                >
                  <Type className="h-4 w-4 text-white" />
                </button>
              )}
            </div>

            {/* Alt text badge */}
            {media.altText && (
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                {t('comp_alt_badge', 'ALT')}
              </div>
            )}
          </div>

          {/* Alt text editor */}
          {editingAlt === media.id && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={media.altText || ''}
                onChange={(e) => updateAltText(media.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setEditingAlt(null); }}
                placeholder={t('compose_alt_text_placeholder', 'Describe this image for accessibility...')}
                className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:ring-1"
                style={{ outlineColor: theme.hex.primaryRing }}
              />
              {media.type === 'image' && (
                <button
                  type="button"
                  onClick={() => handleAutoDescribe(media.id)}
                  disabled={describing}
                  aria-busy={describing}
                  title={t('compose_alt_ai_tip', 'Auto-describe with AI')}
                  className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {describing ? (
                    <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  ) : (
                    <Sparkles className="h-3 w-3" aria-hidden="true" />
                  )}
                  {t('compose_alt_ai_button', 'Describe')}
                </button>
              )}
            </div>
          )}

          {/* Replace file */}
          <div {...getRootProps()} className="mt-2">
            <input {...getInputProps()} />
            <button
              type="button"
              className="w-full text-xs text-gray-500 hover:text-gray-700 py-1.5 border border-dashed border-gray-300 rounded-lg transition-colors"
            >
              {t('compose_replace_file', 'Replace file')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
